import { BlobResponse, IBooleanResponse, IGlobalCache, SecuredRestService } from "shared";
import dayjs from "dayjs";
import {
    IDocumentAttachProps,
    IDocumentPayloadProps,
    IDocumentService,
    IHtmlPreviewResponse,
    IMassUpload,
    IProcessingStatus,
    IVendor,
    IVendorShort
} from "../types/document_service_types";
import { IHrPerson, IHrPersonShort } from "../types/hr_person_types";
import { cleanFormRecord } from "../utils/payload";

export const localStorageFavoritesKey = "favoriteDocuments";
const localRecentlySeenKey = "recentlySeenDocuments";
const recentlySeenLength = 8;

export class DocumentService extends SecuredRestService implements IDocumentService {

    getMassUploads = (id: string, login: string, status: string): Promise<IMassUpload[]> => {
        const dateNow = dayjs().format("DD.MM.YYYY");
        const dateFrom = "01.01.2020";
        const request: Record<string, string> = {
            id,
            dateTo: dateNow,
            dateFrom: dateFrom,
            tzOffset: "-180",
            login,
            status,
            dateProcTo: dateNow,
            dateProcFrom: dateFrom
        };
        return this.get<IMassUpload[]>({
            url: "uploads",
            request: request
        }).then(res => {
            return res;
        });
    };

    getPreviewHtml = (docId: number, extUserId: string | null = null, fileProps: Record<string, File[]>): Promise<IHtmlPreviewResponse> => {
        return this.post<IHtmlPreviewResponse>({
            url: "doc/html",
            request: { docId, extUserId, ...fileProps }
        }).then(res => {
            return res;
        });
    };

    getAutonumberPreview = (
        docId: number,
        values: Record<string, unknown>,
        options?: RequestInit
    ): Promise<Record<string, string>> => {
        const fields = Object.fromEntries(Object.entries(values).filter(([, value]) =>
            typeof value === "string" || typeof value === "number" || typeof value === "boolean"
        ));

        return this.post<Record<string, string> | null | "">({
            url: "autonumber",
            request: { ...fields, docId },
            dataFormat: "formData",
            options
        }).then(response => response || {});
    };

    downloadDocGenerationFile = (uploadId: number, xxcdoc_lang: string): Promise<BlobResponse> => {
        return this.post<BlobResponse>({
            url: "uploads/downloadDocGenerationFile",
            request: { uploadId, xxcdoc_lang },
            options: {
                headers: {
                    "Accept": "application/json",
                }
            }
        }).then(res => {
            return res;
        });
    };

    downloadMassDocTemplate = (docId: number, xxcdoc_lang: string): Promise<BlobResponse> => {
        return this.post<BlobResponse>({
            url: "uploads/massDocGenerationTemplate",
            request: { docId, xxcdoc_lang }
        }).then(res => {
            return res;
        });
    };

    startDocGeneration = (uploadId: number, xxcdoc_lang: string): Promise<IProcessingStatus> => {
        return this.post<IProcessingStatus>({
            url: "uploads/startDocGeneration",
            request: { uploadId, xxcdoc_lang }
        }).then(res => {
            return res;
        });
    };

    changeDocGenerationStatus = (uploadId: number, status: IProcessingStatus): Promise<IProcessingStatus> => {
        return this.post<IProcessingStatus>({
            url: "uploads/changeStatus",
            request: { uploadId, status }
        }).then(res => {
            return res;
        });
    };

    uploadDocGenerationFile = (docId: number, xxcdoc_lang: string, file: File, fileName: string): Promise<IBooleanResponse> => {
        return this.post<IBooleanResponse>({
            url: "uploads/loadDocGenerationFile",
            request: { docId, fileName, file, xxcdoc_lang }
        }).then(res => {
            return res;
        });
    };

    getHrPersonInfo = (login: string): Promise<IHrPerson[]> => {
        return this.get<IHrPerson[]>({
            url: "hr/person",
            request: { value: login }
        }).then(res => {
            if (Array.isArray(res)) {
                return res;
            } else {
                try {
                    return JSON.parse(res) as IHrPerson[];
                } catch (e) {
                    return [];
                }
            }
        });
    };

    getHrPersons = (searchInput: string): Promise<IHrPersonShort[]> => {
        return this.get<IHrPersonShort[]>({
            url: "hr/persons",
            request: { value: searchInput }
        }).then(res => {
            if (Array.isArray(res)) {
                return res;
            } else {
                try {
                    return JSON.parse(res) as IHrPersonShort[];
                } catch (e) {
                    return [];
                }
            }
        });
    };

    getVendorNames = (name: string, extUserId: string | null = null): Promise<IVendorShort[]> => {
        return this.post<IVendorShort[]>({
            url: "vendordata/getNames",
            request: { name, extUserId }
        }).then(res => {
            if (Array.isArray(res)) {
                return res;
            } else {
                try {
                    return JSON.parse(res) as IVendorShort[];
                } catch (e) {
                    return [];
                }
            }
        });
    };

    getVendor = (inn: string, extUserId: string | null = null, globalCache: IGlobalCache): Promise<IVendor> => {
        const request: Record<string, string> = { inn };
        if (extUserId) {
            request["extUserId"] = extUserId;
        }
        const cachedValue = globalCache.getCachedRequest("vendordata", request);
        if (cachedValue) {
            return Promise.resolve(cachedValue);
        } else {
            return this.get<IVendor | string>({
                url: "vendordata",
                request: request
            }).then(res => {
                if (typeof res === "string") {
                    try {
                        const vendor = JSON.parse(res) as IVendor;
                        globalCache.cacheRequest("vendordata", request, vendor);
                        return vendor;
                    } catch (e) {
                        return {} as IVendor;
                    }
                } else {
                    globalCache.cacheRequest("vendordata", request, res);
                    return res;
                }
            });
        }
    };

    attach = ({
                  groupId,
                  docId,
                  docName,
                  docCode,
                  fileName,
                  xxya_cdoc_isnopass,
                  xxya_cdoc_format,
                  xxcdoc_lang,
                  formRecord,
                  startrekQueueAttach,
                  startrekAttachDoc,
                  startrekComment,
                  files,
                  extUserId,
                  filesFieldName
              }: IDocumentAttachProps) => {
        const cleanRecord = cleanFormRecord(formRecord);
        return this.post<string>({
            url: "ticket/attach",
            request: {
                ...cleanRecord,
                docId,
                xxcdoc_lang,
                groupId,
                docName,
                docCode,
                fileName,
                extUserId,
                xxya_cdoc_isnopass,
                xxya_cdoc_format,
                startrekQueueAttach,
                startrekAttachDoc,
                startrekComment
            },
            files,
            filesFieldName
        }).then(res => {
            return res;
        });
    };

    createTicket = ({
                        groupId,
                        docId,
                        docName,
                        docCode,
                        fileName,
                        xxya_cdoc_isnopass,
                        xxya_cdoc_format,
                        xxcdoc_lang,
                        formRecord,
                        files,
                        filesFieldName,
                        extUserId,
                    }: IDocumentPayloadProps) => {
        const cleanRecord = cleanFormRecord(formRecord);
        return this.post<string>({
            url: "ticket/create",
            request: {
                ...cleanRecord,
                extUserId,
                docId,
                xxcdoc_lang,
                groupId,
                docName,
                docCode,
                fileName,
                xxya_cdoc_isnopass,
                xxya_cdoc_format
            },
            files,
            filesFieldName
        }).then(res => {
            return res;
        });
    };
    downloadDocument = ({
                            groupId,
                            docId,
                            docName,
                            docCode,
                            fileName,
                            xxya_cdoc_isnopass,
                            xxya_cdoc_format,
                            xxcdoc_lang,
                            extUserId,
                            formRecord
                        }: IDocumentPayloadProps) => {
        const cleanRecord = cleanFormRecord(formRecord);
        return this.post<BlobResponse>({
            url: "doc",
            request: {
                ...cleanRecord,
                extUserId,
                docId,
                xxcdoc_lang,
                groupId,
                docName,
                docCode,
                fileName,
                xxya_cdoc_isnopass,
                xxya_cdoc_format
            }
        }).then(res => {
            return res;
        });
    };

    setFavorites = (docId: number): Promise<number[]> => {
        let favoritesArr: number[] = [];
        try {
            const favorites = localStorage.getItem(localStorageFavoritesKey);
            if (favorites) {
                favoritesArr = JSON.parse(favorites);
            }
        } catch (e) {
        }
        if (favoritesArr.includes(docId)) {
            favoritesArr = favoritesArr.filter(id => id !== docId);
        } else {
            favoritesArr.push(docId);
        }
        localStorage.setItem(localStorageFavoritesKey, JSON.stringify(favoritesArr));

        return Promise.resolve(favoritesArr);
    };

    getFavorites = (): Promise<number[]> => {
        let favoritesArr: number[] = [];
        try {
            const favorites = localStorage.getItem(localStorageFavoritesKey);
            if (favorites) {
                favoritesArr = JSON.parse(favorites);
            }
        } catch (e) {
        }

        return Promise.resolve(favoritesArr);
    };

    setRecentlySeen = (documentId: number): Promise<number[]> => {
        let recentlySeenArr: number[] = [];
        try {
            const recentlySeen = localStorage.getItem(localRecentlySeenKey);
            if (recentlySeen) {
                recentlySeenArr = JSON.parse(recentlySeen);
            }
        } catch (e) {
        }
        recentlySeenArr.unshift(documentId);
        const recentlySeenSet = new Set<number>();
        recentlySeenArr.forEach((docId) => {
            recentlySeenSet.add(docId);
        });
        let recentlySeenArrFromSet = Array.from(recentlySeenSet);
        if (recentlySeenArrFromSet.length > recentlySeenLength) {
            recentlySeenArrFromSet = recentlySeenArrFromSet.slice(0, recentlySeenLength);
        }
        localStorage.setItem(localRecentlySeenKey, JSON.stringify(recentlySeenArrFromSet));

        return Promise.resolve(recentlySeenArrFromSet);
    };

    getRecentlySeen = (): Promise<number[]> => {
        let recentlySeenArr: number[] = [];
        try {
            const recentlySeen = localStorage.getItem(localRecentlySeenKey);
            if (recentlySeen) {
                recentlySeenArr = JSON.parse(recentlySeen);
            }
        } catch (e) {
        }

        return Promise.resolve(recentlySeenArr);
    };
}
